import { ReactNode, useContext, useMemo } from 'react';

import './App.css';
import Home from './pages/Home';
import Login from './pages/Login';
import { ThemeProvider } from 'styled-components';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import { Auth0Provider } from '@auth0/auth0-react';
import { BrowserRouter as Router, Route, Routes, useNavigate } from 'react-router-dom';
import { TitleProvider } from './contexts/TitleContext';
import { SideMenuProvider } from './contexts/SideMenuContext';
import { PaymentProvider } from './contexts/PaymentContext';
import { ThemeContextProvider, ThemeContext } from './contexts/ThemeContext';
import { ThemeType } from './shared/Theme';
import Models from './components/Models';
import Pricing from './components/Pricing';
import RateLimits from './components/RateLimits';
import BillingDashboard from './components/Dashboard';
import APIKeys from './components/APIKeys';
import Recharge from './components/Recharge';
import Receipts from './components/Receipts';
import NotFound from './pages/NotFound';
import Info from './components/Info';
import BatchAPI from './components/BatchApi';
import Files from './components/Files';
import Organizations from './components/Organizations';
import OrganizationInvitePage from './pages/OrganizationInvite';
import PlaygroundPage from './components/Playground/PlaygroundPage';

declare module '@mui/material/styles' {
    interface Theme {
        variables: ThemeType;
    }
    interface ThemeOptions {
        variables?: ThemeType;
    }
}

type Auth0ProviderWithNavigateProps = {
    children: ReactNode;
};

function Auth0ProviderWithNavigate({ children }: Auth0ProviderWithNavigateProps) {
    const navigate = useNavigate();
    const auth0Domain = process.env.REACT_APP_AUTH0_DOMAIN as string;
    const auth0ClientId = process.env.REACT_APP_AUTH0_CLIENT_ID as string;
    const audience = process.env.REACT_APP_AUTH0_AUDIENCE_URL;

    const onRedirectCallback = (appState?: { returnTo?: string }) => {
        const target = appState?.returnTo || '/';
        navigate(target, { replace: true });
    };

    return (
        <Auth0Provider
            domain={auth0Domain}
            clientId={auth0ClientId}
            cacheLocation='localstorage'
            authorizationParams={{
                redirect_uri: window.location.origin,
                audience,
                scope: "openid profile email chat:messages"
            }}
            onRedirectCallback={onRedirectCallback}
            useRefreshTokens={true}
            useRefreshTokensFallback={true}
        >
            {children}
        </Auth0Provider>
    );
}

function ThemedApp() {
    const { currentTheme, isDarkMode } = useContext(ThemeContext);

    const muiTheme = useMemo(() => createTheme({
        palette: {
            mode: isDarkMode ? 'dark' : 'light',
        },
        variables: {
            ...currentTheme
        }
    }), [isDarkMode, currentTheme]);

    return (
        <MuiThemeProvider theme={muiTheme}>
            <ThemeProvider theme={currentTheme}>
                <TitleProvider>
                    <SideMenuProvider>
                        <PaymentProvider>
                            <Router>
                                <Auth0ProviderWithNavigate>
                                    <div className="App">
                                        <Routes>
                                            <Route path="/" element={<Home />}>
                                                <Route path="home" element={<Info />} />
                                                <Route path="modelos" element={<Models />} />
                                                <Route path="precos" element={<Pricing />} />
                                                <Route path="rate-limits" element={<RateLimits />} />
                                                <Route path="gastos" element={<BillingDashboard />} />
                                                <Route path="chaves-de-api" element={<APIKeys />} />
                                                <Route path="organizacoes" element={<Organizations />} />
                                                <Route path="recarga" element={<Recharge />} />
                                                <Route path="comprovantes" element={<Receipts />} />
                                                <Route path="batch" element={<BatchAPI />} />
                                                <Route path="arquivos" element={<Files />} />
                                                <Route path="playground" element={<PlaygroundPage />} />
                                            </Route>
                                            <Route path="/organizacao-convite" element={<OrganizationInvitePage />} />
                                            <Route path="/auth" element={<Login />} />
                                            <Route path="*" element={<NotFound />} />
                                        </Routes>
                                    </div>
                                </Auth0ProviderWithNavigate>
                            </Router>
                        </PaymentProvider>
                    </SideMenuProvider>
                </TitleProvider>
            </ThemeProvider>
        </MuiThemeProvider>
    );
}

function App() {
    return (
        <ThemeContextProvider>
            <ThemedApp />
        </ThemeContextProvider>
    );
}

export default App;
